import { AppNav, type AppNavItem, type AppNavRow } from '@mdvip/shared/features/app-nav/AppNav';
import { initials } from '@mdvip/shared/lib/initials';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { useAbility } from '@mdvip/shared/stores/authStore';
import type { Subject } from '@mdvip/shared/types/ability';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/AuthContext';

/** CASL subject the provider must be able to read for the entry to render at all. */
type Gated = { requires?: Subject };
type ItemSpec = Omit<AppNavItem, 'label' | 'shortLabel'> & Gated & { labelKey: string; shortLabelKey?: string };
type RowSpec = Omit<AppNavRow, 'label'> & { labelKey: string };

/* `as const satisfies` rather than an annotation: the literal `labelKey` types are what let t()
   key-check the labels, and the annotation would widen them. */
const NAV_ITEMS = [
  { to: '/patients', icon: 'users', labelKey: 'nav.patients', requires: 'Member' },
  { to: '/awp', icon: 'clipboardList', labelKey: 'nav.awp', shortLabelKey: 'nav.awpShort', requires: 'Member' },
  { to: '/reports', icon: 'chartColumn', labelKey: 'nav.reports' },
  { to: '/resources', icon: 'bookOpen', labelKey: 'nav.resources' },
] as const satisfies readonly ItemSpec[];

/* A row with no `to` is an inert placeholder: it renders, and does nothing. Profile, Bookmarks,
   Notifications and Help & Support are all in the design's physician account menu, but have no
   providers destination yet. */
const ACCOUNT_ROWS = [
  { key: 'profile', labelKey: 'nav.profile', icon: 'user', testId: 'account-profile-item' },
  { key: 'staff', labelKey: 'nav.userManagement', icon: 'users', to: '/staff', testId: 'account-staff-item' },
  { key: 'bookmarks', labelKey: 'nav.bookmarks', icon: 'bookmark', testId: 'account-bookmarks-item' },
] as const satisfies readonly RowSpec[];

const MORE_ROWS = [
  { key: 'notifications', labelKey: 'nav.notifications' },
  { key: 'help', labelKey: 'nav.helpSupport' },
] as const satisfies readonly RowSpec[];

/* The label keys are derived back out of the data rather than re-listed, so t() still
   key-checks them: the specs' own `labelKey: string` would widen them. */
type LabelKey =
  | (typeof NAV_ITEMS)[number]['labelKey']
  | Extract<(typeof NAV_ITEMS)[number], { shortLabelKey: string }>['shortLabelKey']
  | (typeof ACCOUNT_ROWS)[number]['labelKey']
  | (typeof MORE_ROWS)[number]['labelKey'];

export function Navigation() {
  const { user, logout } = useAuth();
  const ability = useAbility();
  const { t } = useTranslation('providers');

  // A null ability (permissions not fetched, or the fetch failed) denies, so a gated entry stays
  // hidden rather than flashing in before the rules land — matches apps/members/Navigation.tsx.
  const allowed = (entry: { labelKey: string; requires?: Subject }): boolean => {
    const subject = entry.requires;
    return !subject || (ability?.can('read', subject) ?? false);
  };

  const resolveRow = ({ labelKey, ...row }: RowSpec & { labelKey: LabelKey }): AppNavRow => ({
    ...row,
    label: t(labelKey),
  });
  const resolveItem = ({
    labelKey,
    shortLabelKey,
    requires: _requires,
    ...item
  }: ItemSpec & { labelKey: LabelKey; shortLabelKey?: LabelKey }): AppNavItem => ({
    ...item,
    label: t(labelKey),
    shortLabel: shortLabelKey ? t(shortLabelKey) : undefined,
  });

  const supportPhone = MDVIP_COMPANY_INFO.physicianSupportPhone;
  // The user model carries no role field, so the second line of the account block is derived
  // from `userType` — the only signal distinguishing a physician from practice staff.
  const role = user?.userType === 'staff' ? t('nav.role.staff') : t('nav.role.provider');

  return (
    <AppNav
      /* The practice-side (green) colour scope. `index.html` already sets it on <body>; the chrome
         repeats it so it stays scoped wherever it renders on its own, as in tests. */
      themeClassName="theme-b"
      items={NAV_ITEMS.filter(allowed).map(resolveItem)}
      account={{
        initials: initials({ firstName: user?.firstName, lastName: user?.lastName }),
        name: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
        secondaryLine: role,
        rows: ACCOUNT_ROWS.map(resolveRow),
        moreRows: MORE_ROWS.map(resolveRow),
        support: { phone: supportPhone, callLabel: t('nav.callSupport', { number: supportPhone }) },
        onLogout: () => void logout(),
      }}
      /* Dead hrefs until the pages exist: the design carries both links in the account menu, and
         the portals have no footer to put them in instead. */
      legal={{
        label: t('nav.legal'),
        links: [
          { key: 'privacy-policy', label: t('nav.privacyPolicy'), href: '#' },
          { key: 'privacy-choices', label: t('nav.privacyChoices'), href: '#' },
        ],
      }}
      labels={{
        primary: t('nav.primary'),
        bottomBar: t('nav.bottomBar'),
        accountMenu: t('nav.accountMenu'),
        myAccount: t('nav.myAccount'),
        more: t('nav.more'),
        closeMenu: t('nav.closeMenu'),
        logout: t('nav.logout'),
      }}
    />
  );
}
